'use client';

import { useMemo, memo } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { getVariantImageShapeClassName, ProductType, useClientData } from '@headless-workspace/domain/common/client';
import { CapacityFormat } from '@headless-workspace/domain/common/server';
import { GlowIcon, GlowPalette, GlowSelectorImage, GlowTag, GlowTextBody, Icons } from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { VariantMetadata } from './VariantMetadata';
import { VariantSelector, VariantSelectorProps } from './VariantSelector';

type VariantSelectorZoneProps = VariantSelectorProps & {
    selectedVariantId: string;
    onVariantSelected: (variantId: string) => void;
};

export const VariantSelectorZone: ComponentMemo<VariantSelectorZoneProps> = memo(
    ({
        type,
        variants,
        formats,
        currency,
        enableDiscountInfo,
        priceAlwaysBlack,
        squaredShade,
        locale,
        fontFamily,
        productsSetList,
        isVipBrand,
        ImageTagName,
        setHoveredVariant,
        productName,
        selectedVariantId,
        onVariantSelected,
        isPriceLegalMentionEnabled,
    }) => {
        const { getClientConfigs } = useClientData();
        const miniSizeConfig = getClientConfigs('miniSizeJSON');
        const miniSizeLabel = miniSizeConfig?.miniSizeDisplayPDP ?? '';

        const t = useTranslations('Discover.Pdp.information.selector');

        const isThereMultipleVariants = variants.length > 1;
        const isNotShadeType = type !== ProductType.MULTI_SKU_SHADE_UNI && type !== ProductType.MULTI_SKU_SHADE_DIFF;

        const Selector = useMemo(() => {
            return (
                <>
                    <VariantSelector
                        type={type}
                        variants={variants}
                        formats={formats}
                        currency={currency}
                        enableDiscountInfo={enableDiscountInfo}
                        priceAlwaysBlack={priceAlwaysBlack}
                        squaredShade={squaredShade}
                        locale={locale}
                        fontFamily={fontFamily}
                        productsSetList={productsSetList}
                        isVipBrand={isVipBrand}
                        selectedVariantId={selectedVariantId}
                        onVariantSelected={onVariantSelected}
                        isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                    />
                    {isNotShadeType && (
                        <div className={'hidden'} aria-hidden>
                            {variants.map((variant) => (
                                <VariantMetadata
                                    key={variant.id}
                                    id={variant.id}
                                    name={`${productName} - ${variant.name}`}
                                    url={variant.url}
                                    isAvailable={variant.isAvailable}
                                    price={variant.price}
                                    currency={variant.currency}
                                />
                            ))}
                        </div>
                    )}
                </>
            );
        }, [
            type,
            variants,
            formats,
            currency,
            enableDiscountInfo,
            priceAlwaysBlack,
            squaredShade,
            locale,
            fontFamily,
            productsSetList,
            isVipBrand,
            isNotShadeType,
            productName,
            selectedVariantId,
            onVariantSelected,
            isPriceLegalMentionEnabled,
        ]);

        const Palette = useMemo(() => {
            if (isNotShadeType || !isThereMultipleVariants) {
                return null;
            }
            const shapeClassName = getVariantImageShapeClassName(type, squaredShade);

            const selectedVariant = variants.find((variant) => variant.id === selectedVariantId);

            const previewVariant = (variantId: string) => {
                if (setHoveredVariant) {
                    setHoveredVariant(variantId);
                }
            };

            const resetPreviewVariant = () => {
                if (setHoveredVariant) {
                    setHoveredVariant('');
                }
            };

            return (
                <GlowPalette
                    items={variants.map((variant) => ({
                        id: variant.id,
                        ariaLabel: t('accessibilityText.variant', {
                            variantName: variant.name,
                        }),
                        image: variant.thumbnailImage,
                        isAvailable: variant.isAvailable,
                        structuredData: {
                            price: variant.price,
                            currency: variant.currency,
                            url: variant.url,
                            name: `${productName} - ${variant.name}`,
                        },
                    }))}
                    onClick={onVariantSelected}
                    onMouseEnter={previewVariant}
                    onMouseLeave={resetPreviewVariant}
                    renderItem={({ id, image, isAvailable, structuredData }) => {
                        const isSelected = id === selectedVariant?.id;
                        return (
                            <div
                                className={clsx(
                                    'relative inline-block w-variantImage h-variantImage',
                                    shapeClassName,
                                    isSelected &&
                                        'border-bold border-solid border-border-selected p-paletteSelectedImg overflow-hidden flex items-center justify-center',
                                )}
                            >
                                <VariantMetadata
                                    id={id}
                                    name={structuredData.name}
                                    url={structuredData.url}
                                    isAvailable={isAvailable}
                                    price={structuredData.price}
                                    currency={structuredData.currency}
                                />
                                <GlowSelectorImage
                                    image={image}
                                    ImageTagName={ImageTagName}
                                    isAvailable={isAvailable}
                                    isSelected={isSelected}
                                    shapeClassName={shapeClassName}
                                    squaredShade={squaredShade}
                                />

                                {!isAvailable && (
                                    <div
                                        className={clsx(
                                            'absolute inset-0 flex items-end overflow-hidden',
                                            squaredShade ? 'rounded-0.25' : 'rounded-infinity',
                                        )}
                                    >
                                        <GlowIcon Icon={Icons.OOSBarGroup} className={'w-full h-full'} />
                                    </div>
                                )}
                            </div>
                        );
                    }}
                    previousButtonAriaLabel={t('accessibilityText.prevBtn')}
                    nextButtonAriaLabel={t('accessibilityText.nextBtn')}
                />
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [type, variants, squaredShade, selectedVariantId]);

        if (type === ProductType.PRODUCT_SET || (type === ProductType.MONO_SKU && variants.length === 1)) {
            return Selector;
        }

        const labelMap = {
            [ProductType.MONO_SKU]: t('label.selectMultiFormatTitle'),
            [ProductType.MULTI_SKU_SIZE]: t('label.selectMultiSkuSize', {
                variantCount: variants.length,
            }),
            [ProductType.MULTI_SKU_SHADE_UNI]: t('label.selectMultiSkuShade', {
                variantCount: variants.length,
            }),
            [ProductType.MULTI_SKU_SHADE_DIFF]: t('label.selectMultiSkuShade', {
                variantCount: variants.length,
            }),
        };

        return (
            <div className={'flex flex-col gap-0.75'}>
                {isThereMultipleVariants && <GlowTextBody text={labelMap[type]} fontFamily={fontFamily} />}
                {Selector}
                {formats.includes(CapacityFormat.Mini) && (
                    <GlowTag label={miniSizeLabel} color={'primaryGrey'} size={'medium'} fontFamily={fontFamily} />
                )}
                {Palette}
            </div>
        );
    },
);

VariantSelectorZone.displayName = 'VariantSelectorZone';
